import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiMail, FiLock, FiLogIn } from 'react-icons/fi';
import { AxiosError } from 'axios';
import { useApi } from '../../hooks/useApi';
import { useAuthStore } from '../../stores';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { SimpleFormField } from '../Common/FormField';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuthStore();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  const loginMutation = useApi.useLogin({
    onSuccess: (data) => {
      if (data.token && data.user) {
        setToken(data.token);
        setUser(data.user);
        if (data.user.role === 'master_pending') {
          navigate('/register/pending');
        } else {
          navigate('/dashboard');
        }
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    loginMutation.mutate({ email, password });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0e1016] py-5 lg:py-12 px-4 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <img src="/trade-pilot-logo.png" alt="Trade Pilot" className="lg:h-[120px] lg:w-[120px] h-[110px] w-[110px] object-cover" />
          </div>
          <h2 className="mt-0 lg:mt-0 md:text-4xl text-2xl font-extrabold title-brand-gradient">Trade Pilot</h2>
          <p className="mt-2 text-lg text-gray-600">Welcome Back</p>
          <p className="mt-1 text-sm text-gray-500">Sign in to your account to continue</p>
        </div>

        <div className="card">
          {loginMutation.isError && (
            <ErrorDisplay
              error={
                loginMutation.error instanceof AxiosError
                  ? loginMutation.error.response?.data?.error || 'Login failed. Please try again.'
                  : loginMutation.error?.message || 'Login failed. Please try again.'
              }
              className="mb-4"
            />
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <SimpleFormField
              label="Email Address"
              name="email"
              type="email"
              icon={<FiMail className="h-5 w-5 text-gray-400" />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
            />

            <div className="space-y-2">
              <SimpleFormField
                label="Password"
                name="password"
                type="password"
                icon={<FiLock className="h-5 w-5 text-gray-400" />}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                showPasswordToggle={true}
              />
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  Forgot Password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loginMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <FiLogIn className="h-5 w-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
